import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    DollarSign,
    TrendingUp,
    Clock,
    ShieldCheck,
    Zap,
    Moon,
    Target,
    FileText,
    Sliders,
    Trophy,
    ArrowUpRight,
    Users,
    X,
    ArrowLeft
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { dashboardService, ROIMetrics } from "../services/DashboardService";
import AppLayout from '../layout/AppLayout';
import MobileHeader from '../layout/MobileHeader';

const ROIDashboard: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [metrics, setMetrics] = useState<ROIMetrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter'>('month');
    const [showSimulator, setShowSimulator] = useState(false);

    useEffect(() => {
        fetchMetrics();
    }, [timeRange]);

    const fetchMetrics = async () => {
        setLoading(true);
        try {
            const data = await dashboardService.getROIMetrics(timeRange);
            console.log("Fetched metrics:", data); // Debug log
            setMetrics(data);
        } catch (error) {
            console.error("Failed to fetch ROI metrics:", error);
            // Set mock data on error so dashboard still displays
            setMetrics(null as unknown as ROIMetrics);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <AppLayout>
                <div className="flex items-center justify-center h-screen bg-main-bg">
                    <div className="animate-pulse text-gray-400">
                        {t("dashboard.loading")}
                    </div>
                </div>
            </AppLayout>
        );
    }

    if (!metrics) {
        return (
            <AppLayout>
                <div className="flex items-center justify-center h-screen bg-main-bg text-gray-400">
                    {t("dashboard.noData")}
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout pageTitle={t("dashboard.title")}>
            <MobileHeader
                title={t("dashboard.title")}
                showBack={false}
                onBackClick={() => navigate('/')}
            />

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden bg-main-bg min-h-full">
                <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full h-full">
                    {/* Desktop controls bar */}
                    <div className="hidden sm:flex items-center justify-end gap-2 px-4 py-2 bg-white border-b border-gray-100 flex-shrink-0">
                        <div className="flex items-center gap-3">
                            {metrics.scope === 'organization' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                                    <Users className="w-3 h-3" />
                                    {t("dashboard.scopeBadge")}
                                </span>
                            )}
                            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                                {(['week', 'month'] as const).map((range) => (
                                    <button
                                        key={range}
                                        onClick={() => setTimeRange(range)}
                                        className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${timeRange === range
                                                ? 'bg-white text-gray-900 shadow-sm'
                                                : 'text-gray-600 hover:text-gray-900'
                                            }`}
                                    >
                                        {t(`dashboard.timeRange.${range}`)}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={() => setShowSimulator(!showSimulator)}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors"
                            >
                                <Sliders className="w-3.5 h-3.5" />
                                <span>{t("dashboard.simulatorButton")}</span>
                            </button>
                        </div>
                    </div>

                    {/* Mobile-only controls bar */}
                    <div className="sm:hidden flex items-center justify-between gap-2 px-4 py-2 bg-white border-b border-gray-100 flex-shrink-0">
                        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                            {(['week', 'month'] as const).map((range) => (
                                <button
                                    key={range}
                                    onClick={() => setTimeRange(range)}
                                    className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${timeRange === range ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                                >
                                    {t(`dashboard.timeRange.${range}`)}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setShowSimulator(!showSimulator)}
                            className="flex items-center justify-center p-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                        >
                            <Sliders className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    {/* Content - Fit to screen without scrolling */}
                    <div className="flex-1 overflow-y-auto px-4 py-3">
                        <div className="space-y-3">
                            {/* Top Row - Bottom Line Widget */}
                            <BottomLineWidget metrics={metrics} />

                            {/* Second Row - Revenue Engine + Productivity */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                <RevenueEngineWidget metrics={metrics} />
                                <ProductivityWidget metrics={metrics} />
                            </div>

                            {/* Third Row - Quality Control + Win Feed */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                                <div className="lg:col-span-2">
                                    <QualityControlWidget metrics={metrics} />
                                </div>
                                <WinFeedWidget wins={metrics.recentWins || []} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ROI Simulator Modal */}
            {showSimulator && (
                <ROISimulator
                    onClose={() => setShowSimulator(false)}
                    currentMetrics={metrics}
                />
            )}
        </AppLayout>
    );
};

/* ==================== BOTTOM LINE WIDGET ==================== */
interface BottomLineWidgetProps {
    metrics: ROIMetrics;
}

const BottomLineWidget: React.FC<BottomLineWidgetProps> = ({ metrics }) => {
    const { t } = useTranslation();

    // Safe access to bottomLine with fallback values
    const bottomLine = metrics.bottomLine || {
        totalValueGenerated: 0,
        roiMultiplier: 0,
        paybackDays: 0,
        laborSavings: 0
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-4">
                <div className="p-1.5 bg-green-50 rounded-lg">
                    <DollarSign className="w-4 h-4 text-green-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">
                    {t("dashboard.bottomLine.title")}
                </h2>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Total Value Generated */}
                <div className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                    <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">
                        {t("dashboard.bottomLine.totalValue")}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        ${bottomLine.totalValueGenerated.toLocaleString()}
                    </p>
                </div>

                {/* ROI Multiplier */}
                <div className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                    <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">
                        {t("dashboard.bottomLine.roiMultiplier")}
                    </p>
                    <div className="flex items-baseline gap-2">
                        <p className="text-2xl font-semibold text-gray-900">{bottomLine.roiMultiplier}×</p>
                        <TrendingUp className="w-4 h-4 text-green-600" />
                    </div>
                </div>

                {/* Payback Period */}
                <div className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                    <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">
                        {t("dashboard.bottomLine.payback")}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {bottomLine.paybackDays}
                        <span className="text-sm text-gray-600 ml-1">days</span>
                    </p>
                    <div className="mt-2 bg-gray-200 rounded-full h-1">
                        <div
                            className="bg-green-600 rounded-full h-1 transition-all duration-1000"
                            style={{ width: `${Math.min(100, (bottomLine.paybackDays / 30) * 100)}%` }}
                        />
                    </div>
                </div>

                {/* Labor Savings */}
                <div className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                    <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">
                        {t("dashboard.bottomLine.laborSavings")}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        ${bottomLine.laborSavings.toLocaleString()}
                    </p>
                </div>
            </div>
        </div>
    );
};

/* ==================== REVENUE ENGINE WIDGET ==================== */
interface RevenueEngineWidgetProps {
    metrics: ROIMetrics;
}

const RevenueEngineWidget: React.FC<RevenueEngineWidgetProps> = ({ metrics }) => {
    const { t } = useTranslation();

    // Safe access to revenue with fallback values
    const revenue = metrics.revenue || {
        aiUpsellRevenue: 0,
        aiUpsellTrend: 0,
        topUpsells: [],
        speedToLeadRevenue: 0,
        afterHoursRevenue: 0,
        afterHoursOrders: 0,
        baselineWinRate: 0,
        currentWinRate: 0
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-blue-50 rounded-lg">
                    <Zap className="w-4 h-4 text-blue-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">
                    {t("dashboard.revenue.title")}
                </h2>
            </div>

            <div className="space-y-2">
                {/* AI Upsell Revenue */}
                <MetricCard
                    label={t("dashboard.revenue.aiUpsell")}
                    value={`$${revenue.aiUpsellRevenue.toLocaleString()}`}
                    icon={<TrendingUp className="w-4 h-4 text-blue-600" />}
                    trend={revenue.aiUpsellTrend}
                />

                {/* Top Upsells */}
                {revenue.topUpsells.length > 0 && (
                    <div className="border border-gray-100 rounded-lg p-3">
                        <p className="text-[10px] text-gray-500 mb-2 uppercase tracking-wide">
                            {t("dashboard.revenue.topUpsells")}
                        </p>
                        <div className="space-y-1">
                            {revenue.topUpsells.slice(0, 3).map((upsell: { item: string; count: number }, index: number) => (
                                <div key={index} className="flex justify-between items-center text-xs">
                                    <span className="text-gray-700 flex items-center gap-1.5 truncate">
                                        <Trophy className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                                        <span className="truncate">{upsell.item}</span>
                                    </span>
                                    <span className="font-medium text-gray-900 ml-2">{upsell.count}×</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Speed-to-Lead Revenue */}
                <MetricCard
                    label={t("dashboard.revenue.speedToLead")}
                    value={`$${revenue.speedToLeadRevenue.toLocaleString()}`}
                    icon={<Target className="w-4 h-4 text-green-600" />}
                />

                {/* While You Slept */}
                <MetricCard
                    label={t("dashboard.revenue.afterHours")}
                    value={`$${revenue.afterHoursRevenue.toLocaleString()}`}
                    icon={<Moon className="w-4 h-4 text-indigo-600" />}
                    subtitle={`${revenue.afterHoursOrders} orders processed 5PM-8AM`}
                />

                {/* Win Rate */}
                <div className="border border-gray-100 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 mb-2 uppercase tracking-wide">
                        {t("dashboard.revenue.winRate")}
                    </p>
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <div className="flex justify-between text-[10px] mb-1 text-gray-600">
                                <span>Baseline: {revenue.baselineWinRate}%</span>
                                <span className="font-semibold text-green-700">Current: {revenue.currentWinRate}%</span>
                            </div>
                            <div className="bg-gray-100 rounded-full h-1.5">
                                <div
                                    className="bg-green-600 rounded-full h-1.5 transition-all"
                                    style={{ width: `${revenue.currentWinRate}%` }}
                                />
                            </div>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-green-600 flex-shrink-0" />
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ==================== PRODUCTIVITY WIDGET ==================== */
interface ProductivityWidgetProps {
    metrics: ROIMetrics;
}

const ProductivityWidget: React.FC<ProductivityWidgetProps> = ({ metrics }) => {
    const { t } = useTranslation();

    // Safe access to productivity with fallback values
    const productivity = metrics.productivity || {
        hoursSaved: 0,
        fteEquivalent: 0,
        automationRate: 0,
        avgResponseSeconds: 0,
        industryAvgHours: 0,
        totalQuotesProcessed: 0
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-purple-50 rounded-lg">
                    <Clock className="w-4 h-4 text-purple-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">
                    {t("dashboard.productivity.title")}
                </h2>
            </div>

            <div className="space-y-2">
                {/* Human Hours Saved */}
                <div className="text-center border border-gray-100 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">
                        {t("dashboard.productivity.hoursSaved")}
                    </p>
                    <p className="text-3xl font-semibold text-purple-600 mb-1">
                        {productivity.hoursSaved}
                    </p>
                    <div className="flex items-center justify-center gap-1.5 text-xs text-gray-600">
                        <Users className="w-3 h-3" />
                        <span>
                            {t("dashboard.productivity.fteEquivalent", {
                                count: productivity.fteEquivalent
                            })}
                        </span>
                    </div>
                </div>

                {/* Automation Rate */}
                <div className="border border-gray-100 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-2">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                            {t("dashboard.productivity.automationRate")}
                        </p>
                        <span className="text-lg font-semibold text-purple-600">
                            {productivity.automationRate}%
                        </span>
                    </div>
                    <div className="bg-gray-100 rounded-full h-1.5">
                        <div
                            className="bg-purple-600 rounded-full h-1.5 transition-all duration-1000 flex items-center justify-end pr-0.5"
                            style={{ width: `${productivity.automationRate}%` }}
                        >
                            <Zap className="w-2.5 h-2.5 text-white" />
                        </div>
                    </div>
                </div>

                {/* Response Time Comparison */}
                <div className="border border-gray-100 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 mb-2 uppercase tracking-wide">
                        {t("dashboard.productivity.responseTime")}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="text-center">
                            <p className="text-[10px] text-gray-500 mb-0.5">CATA AI</p>
                            <p className="text-xl font-semibold text-green-600">
                                {productivity.avgResponseSeconds}s
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-[10px] text-gray-500 mb-0.5">Industry Avg</p>
                            <p className="text-xl font-semibold text-gray-400">
                                {productivity.industryAvgHours}h
                            </p>
                        </div>
                    </div>
                </div>

                {/* Total Quotes Processed */}
                <MetricCard
                    label={t("dashboard.productivity.totalQuotes")}
                    value={productivity.totalQuotesProcessed.toLocaleString()}
                    icon={<FileText className="w-4 h-4 text-purple-600" />}
                />
            </div>
        </div>
    );
};

/* ==================== QUALITY CONTROL WIDGET ==================== */
interface QualityControlWidgetProps {
    metrics: ROIMetrics;
}

const QualityControlWidget: React.FC<QualityControlWidgetProps> = ({ metrics }) => {
    const { t } = useTranslation();

    // Safe access to quality with fallback values
    const quality = metrics.quality || {
        errorPreventionSavings: 0,
        vinMatchAccuracy: 0,
        avgCustomerRating: 0
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-green-50 rounded-lg">
                    <ShieldCheck className="w-4 h-4 text-green-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">
                    {t("dashboard.quality.title")}
                </h2>
            </div>

            <div className="grid grid-cols-3 gap-2">
                {/* Error Prevention Savings */}
                <div className="border border-gray-100 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">
                        {t("dashboard.quality.errorSavings")}
                    </p>
                    <p className="text-xl font-semibold text-green-600">
                        ${quality.errorPreventionSavings.toLocaleString()}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-1">
                        {t("dashboard.quality.returnsSaved")}
                    </p>
                </div>

                {/* VIN Match Accuracy */}
                <div className="border border-gray-100 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">
                        {t("dashboard.quality.vinAccuracy")}
                    </p>
                    <div className="flex items-center gap-1">
                        <p className="text-xl font-semibold text-blue-600">
                            {quality.vinMatchAccuracy}%
                        </p>
                        <ShieldCheck className="w-4 h-4 text-blue-600" />
                    </div>
                </div>

                {/* Customer Satisfaction */}
                <div className="border border-gray-100 rounded-lg p-3">
                    <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">
                        {t("dashboard.quality.satisfaction")}
                    </p>
                    <div className="flex items-center gap-1">
                        <p className="text-xl font-semibold text-yellow-600">
                            {quality.avgCustomerRating}
                        </p>
                        <span className="text-yellow-500 text-xs">★★★★★</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ==================== WIN FEED WIDGET ==================== */
interface WinFeedWidgetProps {
    wins: Array<{
        id: string;
        type: 'upsell' | 'speed' | 'automation' | 'afterhours';
        message: string;
        value?: number;
        timestamp: string;
    }>;
}

const WinFeedWidget: React.FC<WinFeedWidgetProps> = ({ wins }) => {
    const { t } = useTranslation();

    const getIcon = (type: string) => {
        switch (type) {
            case 'upsell': return <TrendingUp className="w-3 h-3 text-green-600" />;
            case 'speed': return <Zap className="w-3 h-3 text-blue-600" />;
            case 'automation': return <Target className="w-3 h-3 text-purple-600" />;
            case 'afterhours': return <Moon className="w-3 h-3 text-indigo-600" />;
            default: return <Trophy className="w-3 h-3 text-yellow-600" />;
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-yellow-50 rounded-lg">
                    <Trophy className="w-4 h-4 text-yellow-600" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">
                    {t("dashboard.wins.title")}
                </h2>
            </div>

            <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {wins.slice(0, 5).map((win) => (
                    <div
                        key={win.id}
                        className="border border-gray-100 rounded-lg p-2 hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-start gap-1.5">
                            {getIcon(win.type)}
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-700 line-clamp-2">{win.message}</p>
                                {win.value && (
                                    <p className="text-[10px] font-medium text-green-600 mt-0.5">
                                        +${win.value}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ==================== ROI SIMULATOR MODAL ==================== */
interface ROISimulatorProps {
    onClose: () => void;
    currentMetrics: ROIMetrics;
}

const ROISimulator: React.FC<ROISimulatorProps> = ({ onClose, currentMetrics }) => {
    const { t } = useTranslation();

    // Safe access to simulatorDefaults with fallback values
    const simulatorDefaults = currentMetrics.simulatorDefaults || {
        avgRepairValue: 350,
        employeeCostPerHour: 45,
        quotesPerDay: 25
    };

    const [avgRepairValue, setAvgRepairValue] = useState(simulatorDefaults.avgRepairValue);
    const [employeeCost, setEmployeeCost] = useState(simulatorDefaults.employeeCostPerHour);
    const [quotesPerDay, setQuotesPerDay] = useState(simulatorDefaults.quotesPerDay);

    const calculateProjectedROI = () => {
        const monthlyQuotes = quotesPerDay * 30;
        const laborSavingsPerQuote = (10 / 60) * employeeCost; // 10 min saved per quote
        const totalLaborSavings = monthlyQuotes * laborSavingsPerQuote;
        const upsellRevenue = monthlyQuotes * avgRepairValue * 0.15; // 15% avg upsell
        const totalValue = totalLaborSavings + upsellRevenue;
        const subscriptionCost = 299; // Example
        const roi = Math.round(totalValue / subscriptionCost);

        return { totalValue: Math.round(totalValue), roi };
    };

    const projected = calculateProjectedROI();

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl border border-gray-200 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold text-gray-900">
                            {t("dashboard.simulator.title")}
                        </h2>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    <div className="space-y-6">
                        {/* Sliders */}
                        <SliderInput
                            label={t("dashboard.simulator.avgRepair")}
                            value={avgRepairValue}
                            onChange={setAvgRepairValue}
                            min={50}
                            max={1000}
                            step={10}
                            prefix="$"
                        />

                        <SliderInput
                            label={t("dashboard.simulator.employeeCost")}
                            value={employeeCost}
                            onChange={setEmployeeCost}
                            min={15}
                            max={100}
                            step={5}
                            prefix="$"
                        />

                        <SliderInput
                            label={t("dashboard.simulator.quotesPerDay")}
                            value={quotesPerDay}
                            onChange={setQuotesPerDay}
                            min={5}
                            max={100}
                            step={5}
                        />

                        {/* Results */}
                        <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">
                                {t("dashboard.simulator.projected")}
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">Total Value</p>
                                    <p className="text-2xl font-semibold text-gray-900">
                                        ${projected.totalValue.toLocaleString()}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">ROI Multiplier</p>
                                    <p className="text-2xl font-semibold text-green-600">
                                        {projected.roi}×
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ==================== REUSABLE COMPONENTS ==================== */
interface MetricCardProps {
    label: string;
    value: string;
    icon: React.ReactNode;
    trend?: number;
    subtitle?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, icon, trend, subtitle }) => {
    return (
        <div className="border border-gray-100 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</p>
                {icon}
            </div>
            <div className="flex items-baseline gap-1.5">
                <p className="text-lg font-semibold text-gray-900">{value}</p>
                {trend !== undefined && (
                    <span className={`text-[10px] font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {trend >= 0 ? '+' : ''}{trend}%
                    </span>
                )}
            </div>
            {subtitle && <p className="text-[10px] text-gray-500 mt-1">{subtitle}</p>}
        </div>
    );
};

interface SliderInputProps {
    label: string;
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
    step: number;
    prefix?: string;
}

const SliderInput: React.FC<SliderInputProps> = ({ label, value, onChange, min, max, step, prefix = '' }) => {
    return (
        <div>
            <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">{label}</label>
                <span className="text-sm font-semibold text-gray-900">{prefix}{value}</span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
            />
        </div>
    );
};

export default ROIDashboard;
